import type { RadioChangeEvent } from 'antd';
import { EllipsisVertical, Plus } from 'lucide-react';

import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import Modal from '@/components/Modal/Modal';
import {
  CHART_CALCULATION,
  CHART_FIELD,
  CHART_GRAPH,
  CHART_METRIC,
  CHART_METRIC_VIEW_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import {
  isCalculationTypeEnabled,
  isCellCalculationSegmentEnabled,
  isShowRowTotalEnabled,
} from '@/features/(charts)/chart-renderer/lib/conditions';
import ColorScaleModal from '@/features/(charts)/chart-renderer/ui/chart-setting-modal/ColorScaleModal';
import ScaleOptionModal from '@/features/(charts)/chart-renderer/ui/chart-setting-modal/ScaleOptionModal';
import useModalState from '@/hooks/pure/useModalState';

import useChartStateAction from '../../hooks/use-chart-state-action/useChartStateAction';
import { Pane } from './ControllerPane';
import { SeriesItem } from './SeriesItem';

type Props = {
  chartInfo: ChartContextProps;
};

const ChartSeriesManager = (props: Props) => {
  const { chartInfo } = props;
  const { state, setState } = chartInfo;
  const {
    onViewTypeChange,
    onScaleOptionChange,
    onColorScaleChange,
    onShareCalculationTypeChange,
    onShareCellCalculationSegmentChange,
    onShowRowTotalChange,
  } = useChartStateAction(chartInfo);

  // 다중 시리즈가 허용되는지 확인
  const isMultiSeriesAllowed =
    (state.type === CHART_TYPE.SINGLE_AXIS &&
      state.graph === CHART_GRAPH.COMBO_CHART) ||
    (state.type === CHART_TYPE.CROSS_AXIS && state.graph === CHART_GRAPH.TABLE);

  const addSeries = () => {
    if (!isMultiSeriesAllowed) return;

    const newSeries: ChartSeriesDef = {
      cross_field: {
        type: CHART_FIELD.FEEDBACK_ALL,
        entire: true,
        candidates: [],
      },
      measure: {
        metric: CHART_METRIC.FEEDBACK,
        calculation: CHART_CALCULATION.COUNT,
      },
      options: {
        view_type: CHART_METRIC_VIEW_TYPE.COUNT,
      },
    };

    setState((prev) => ({
      ...prev,
      series: [...prev.series, newSeries] as ChartSeriesDef[],
    }));
  };

  const removeSeries = (index: number) => {
    if (state.series.length <= 1) return;

    setState((prev) => ({
      ...prev,
      series: prev.series.filter((_, i) => i !== index),
    }));
  };

  const onViewTypeChangeHandler = (index: number, e: RadioChangeEvent) =>
    onViewTypeChange(index, e.target.value as CHART_METRIC_VIEW_TYPE);

  const {
    status: scaleOptionModalStatus,
    open: openScaleOptionModal,
    close: closeScaleOptionModal,
    modalState: scaleOptionModalState,
  } = useModalState({
    seriesIndex: 0,
  });

  const {
    status: colorScaleModalStatus,
    open: openColorScaleModal,
    close: closeColorScaleModal,
    modalState: colorScaleModalState,
  } = useModalState({
    seriesIndex: 0,
  });

  return (
    <div className="flex flex-col gap-3">
      {state.series.map((series: ChartSeriesDef, index: number) => {
        const calculationTypeEnabled = isCalculationTypeEnabled(
          state.type,
          state.graph,
          series.options?.view_type,
        );

        const cellCalculationSegmentEnabled = isCellCalculationSegmentEnabled(
          calculationTypeEnabled,
          series.options?.share_calculation_type,
        );

        const showRowTotalEnabled = isShowRowTotalEnabled(
          state.graph,
          series.options?.view_type,
        );

        return (
          <Pane
            key={index}
            initialOpen={index === 0}
            title={`Data set (Column) ${index > 0 ? index + 1 : ''}`}
            titleAction={
              <Menu
                triggerNode={
                  <IconButton
                    outlined={false}
                    icon={<EllipsisVertical className="size-3 text-gray-700" />}
                    sizeType="s"
                  />
                }
                options={[
                  {
                    label: 'Color scale',
                    onClick: () =>
                      openColorScaleModal({
                        modalState: { seriesIndex: index },
                      }),
                    disabled:
                      props.chartInfo.state.graph === CHART_GRAPH.COMBO_CHART ||
                      props.chartInfo.state.graph !== CHART_GRAPH.TABLE,
                  },
                  {
                    label: 'Scale option',
                    onClick: () =>
                      openScaleOptionModal({
                        modalState: { seriesIndex: index },
                      }),
                    disabled:
                      series.options?.view_type ===
                        CHART_METRIC_VIEW_TYPE.PERCENTAGE ||
                      props.chartInfo.state.graph === CHART_GRAPH.COMBO_CHART,
                  },
                  {
                    label: 'Remove',
                    className: 'text-destructive',
                    onClick: () => removeSeries(index),
                    disabled: state.series.length <= 1,
                  },
                ]}
              />
            }
          >
            <SeriesItem
              key={index}
              chartInfo={chartInfo}
              series={series}
              index={index}
              calculationTypeEnabled={calculationTypeEnabled}
              cellCalculationSegmentEnabled={cellCalculationSegmentEnabled}
              showRowTotalEnabled={showRowTotalEnabled}
              onViewTypeChange={onViewTypeChangeHandler}
              onShareCalculationTypeChange={onShareCalculationTypeChange}
              onShowRowTotalChange={onShowRowTotalChange}
              onShareCellCalculationSegmentChange={
                onShareCellCalculationSegmentChange
              }
            />
          </Pane>
        );
      })}

      {isMultiSeriesAllowed && (
        <Button
          variant="outlined"
          leftIcon={<Plus />}
          onClick={addSeries}
          sizeType="m"
          disabled={state.series.length >= 5 || state.target.is_compare}
          tooltip={
            state.target.is_compare
              ? 'Disable date range comparison to add more data sets'
              : state.series.length >= 5
                ? 'Maximum 5 data sets reached. Remove one to add another.'
                : undefined
          }
        >
          Add data set
        </Button>
      )}
      <Modal status={scaleOptionModalStatus} closeModal={closeScaleOptionModal}>
        <ScaleOptionModal
          scale={
            typeof state.series[scaleOptionModalState.seriesIndex].options
              ?.scale_min === 'number' &&
            typeof state.series[scaleOptionModalState.seriesIndex].options
              ?.scale_max === 'number'
              ? {
                  min:
                    state.series[scaleOptionModalState.seriesIndex].options
                      ?.scale_min || 0,
                  max:
                    state.series[scaleOptionModalState.seriesIndex].options
                      ?.scale_max || 0,
                }
              : undefined
          }
          onDoneClick={(scale) =>
            onScaleOptionChange(scaleOptionModalState.seriesIndex, scale)
          }
        />
      </Modal>
      <Modal status={colorScaleModalStatus} closeModal={closeColorScaleModal}>
        <ColorScaleModal
          scale={
            state.series[colorScaleModalState.seriesIndex].options?.color_scale
          }
          onDoneClick={(scale) =>
            onColorScaleChange(colorScaleModalState.seriesIndex, scale)
          }
        />
      </Modal>
    </div>
  );
};

export default ChartSeriesManager;
